import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ContentState } from "draft-js";
import { Input, RadioInput, Select } from "@jhool-io/fe-components";
import { IRequestChangePayload } from "../../../modules/notes/types/notes.types";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import NoteDetails from "../NotesDetails/NoteDetails";
import useToast from "../../../hooks/useToast";
import { useAppSelector } from "../../../hooks/useRedux";
import { useRequestChange } from "../../../hooks/mutations/note";
import { AddTextEditor } from "../../TextEditor/AddTextEditor/AddTextEditor";
import styles from "../../../modules/notes/components/NoteContent/NoteContent.module.scss";
import { getChangeRequestSources } from "../../../modules/notes/helpers/notes.helpers";
import { makeStringFirstLetterCapital } from "../../../utils/helpers";
import XCircleIcon from "../../../icons/XCircle";

interface RequestChangesProps {
    // Function to call when form submit button is clicked
    onFormSubmit(): void;
}

const schema = yup.object({
    source: yup.string().nullable().required("Choose who this request is from"),
    request_reason: yup.string().required("Request reason is required"),
    other_reason: yup
        .string()
        .when("request_reason", (request_reason, field) =>
            request_reason === "Other"
                ? field.required("Other reason is required")
                : field
        ),
});

type Option = {
    label: React.ReactNode;
    value: string;
};

export default function RequestChanges({ onFormSubmit }: RequestChangesProps) {
    const OptionsForChanges = [
        "Duplicate note / needs archiving",
        "Failure to meet regulatory requirements",
        "Incomplete or missing information",
        "Incorrect information",
        "Insufficient justification",
        "Lack of detail",
        "Repetitive / copied content",
        "Treatment plan / goals",
        "Unclear / non-clinical language",
        "Unnecessary / sensitive detail",
        "Wrong / unspecific diagnosis",
        "Wrong note type",
        "Wrong session details",
        "Other reason",
    ].map((reason) => ({
        label: reason,
        value: reason,
    }));

    const { practice } = useAppSelector((state) => state.userPractice);
    const sources = getChangeRequestSources(practice);

    const {
        register,
        watch,
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<IRequestChangePayload>({
        resolver: yupResolver(schema),
        mode: "onChange",
        defaultValues: {
            source: sources.length === 1 ? sources[0] : undefined,
        },
    });

    const OtherReasons = watch("request_reason", "");

    const params = useParams();
    const noteId = params.noteId as string;
    const clientId = params.clientId as string;
    const [description, setDescription] = React.useState("");
    const [isDescriptionProvided, setIsDescriptionProvided] =
        useState<boolean>(true);
    const [getCurrentContent, setGetCurrentContent] = useState<ContentState>();

    const { toast } = useToast();

    // Query client
    const queryClient = useQueryClient();

    const requestChange = useRequestChange(noteId);

    const onSubmit = (data: IRequestChangePayload) => {
        if (!getCurrentContent?.hasText()) {
            // Description is empty
            setIsDescriptionProvided(false);
            return;
        }
        setIsDescriptionProvided(true);
        const dataToSend = {
            request_details: description,
            request_reason:
                data.request_reason === "Other reason"
                    ? (data.other_reason as string)
                    : (data.request_reason as string),
            source: data.source,
        };
        requestChange.mutate(dataToSend, {
            onSuccess: async (res) => {
                await queryClient.invalidateQueries({
                    queryKey: [noteId, `change-request`],
                });
                await queryClient.invalidateQueries({
                    queryKey: [clientId, `session-note`, noteId],
                });
                await queryClient.invalidateQueries({
                    queryKey: [`todos`],
                });

                toast({
                    mode: "success",
                    message: res.message || "Changes requested successfully",
                });
                onFormSubmit();
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not request for changes at this time",
                });
            },
        });
    };

    return (
        <form id="request-changes" onSubmit={handleSubmit(onSubmit)}>
            <div className="fg">
                <NoteDetails showProvider />
            </div>
            <div className="fg flex flex-col gap-y-1">
                <p className="font-medium !text-caption text-textSec flex items-center gap-x-1">
                    Requesting as
                </p>
                <div className="flex gap-[--spacing-items] items-center">
                    {sources.map((source) => (
                        <RadioInput
                            key={source}
                            {...register("source")}
                            label={makeStringFirstLetterCapital(source)}
                            value={source}
                        />
                    ))}
                </div>
                {errors.source ? (
                    <span className="!text-danger text-[11px] flex items-center gap-x-1">
                        <XCircleIcon className="size-3" />
                        {errors.source.message}
                    </span>
                ) : null}
            </div>
            <div className="fg">
                <Controller
                    name="request_reason"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Reason for request"
                            placeholder="Reason for request"
                            onChange={(val) =>
                                field.onChange((val as Option).value)
                            }
                            options={OptionsForChanges}
                            hasError={!!errors.request_reason}
                            errorText={errors.request_reason?.message}
                        />
                    )}
                />
            </div>
            {OtherReasons === "Other reason" && (
                <div className="fg">
                    <Input
                        {...register("other_reason")}
                        aria-label="other-reasons"
                        label="Specify Other"
                        placeholder="Specify Other"
                        hasError={!!errors.request_reason}
                        errorText={errors.request_reason?.message}
                    />
                </div>
            )}
            <div className="fg">
                <AddTextEditor
                    title="Description"
                    onEditorTextChange={setDescription}
                    setIsRequiredFieldProvided={setGetCurrentContent}
                    isRequiredFieldProvided={
                        isDescriptionProvided || getCurrentContent?.hasText()
                    }
                    titleClass="font-medium text-xs text-black leading-none"
                />
                {!isDescriptionProvided && !getCurrentContent?.hasText() && (
                    <div className={styles.error}>Description is required</div>
                )}
            </div>
        </form>
    );
}
